import React, {Component, ReactNode} from 'react'
import {createResidentPage, ResidentPagePropsSet} from '../shared/hoc/createResidentPage'
import ReservationsRepository from './ReservationsRepository'
import EventEmitterExResident from '../shared/pageBaseComponents/EventEmitterExResident'
import FacilityForReservation from '../facilities/FacilityForReservation'
import {NewDurationRentalFacilityReservation, NewOvernightRentalFacilityReservation} from './ReserveFacilityModel'
import {DisplayItemDividerForPageTop} from '../../styledComponents/DisplayItem'
import {
    CenterPlainLinkStyledButton,
    PlainLinkStyledBackButton,
    PrimaryStyledButton,
} from '../../styledComponents/Button'
import {NatAsyncCallFunction} from '../../__NoAutomatedTests/common/util/Natutil'
import {HttpError} from '../../network/errors/HttpError'
import {ErrorResponseBody} from '../../network/response/ErrorResponseBody'
import {RequestError} from '../../network/response/RequestError'
import {Messages} from '../../Messages'
import {AVAILABLE_OR_NOT, RESERVATION_TYPE} from '../../CodeMaster'
import ResidentReserveFacilityConfirmComponent, {
    ReserveFacilityConfirmData,
} from './ResidentReserveFacilityConfirmComponent'
import {StyledPageTitle} from './components/CommonStyle'
import {BlockSpacer} from '../../styledComponents/ResponsiveConvenience'
import {
    setResidentMyReservationsPageSearchDate,
} from './ResidentMyReservationsComponents/RMRReservationStatusesComponent'
import {
    clearResidentReserveTopPageSearchConditions,
} from './ResidentReserveFacilityTopComponents/RRFTFacilitySearchComponent'
import {
    clearResidentFacilityReservationStatusPageFilterConditions,
    clearResidentFacilityReservationStatusPageSearchDate,
} from './ResidentFacilityReservationStatusPage'
import moment, {Moment} from 'moment'
import {Nullable, Optional} from '../../app/TypeHelpers'
import NatCurrentDateTimeRepo
    from '../../__NoAutomatedTests/components/currentDateTime/repository/NatCurrentDateTimeRepo'
import {convertToMomentOrError, isCancelLimitOver} from './helpers/helpers'
import styled from 'styled-components'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faExclamationCircle} from '@fortawesome/free-solid-svg-icons'
import {Box} from '@mui/material'
import {FontSize, FontWeights} from '../../styledComponents/Constants'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    facilityId: number
    reservationsRepo: ReservationsRepository
    currentDateTimeRepo: NatCurrentDateTimeRepo
    eventEmitter: EventEmitterExResident
    location: Location<LocationState>
}

interface LocationState {
    previousPageUrl: string
    stayAvailable: boolean
    facilityInfo: FacilityForReservation
    reserveInfo: NewOvernightRentalFacilityReservation | NewDurationRentalFacilityReservation
}

interface State {
    currentDateTime: Nullable<Moment>
}

class ResidentReserveFacilityInputConfirmPage extends Component<Props & ResidentPagePropsSet, State> {
    constructor(props: Readonly<Props & ResidentPagePropsSet>) {
        super(props)

        this.state = {
            currentDateTime: null,
        }

        this.handleRegisterButtonClick = this.handleRegisterButtonClick.bind(this)
        this.handleBackLinkOnClick = this.handleBackLinkOnClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            const currentDateTime: Nullable<Moment> = await this.getCurrentDate()
            if (currentDateTime === null) {
                return
            }
            this.setState({
                currentDateTime: currentDateTime,
            })
        } finally {
            this.props.eventEmitter.closeLoading()
        }
        return
    }

    async handleBackLinkOnClick(): Promise<void> {
        const {navigate, condominiumCd, facilityId} = this.props
        const isStayAvailable: boolean = this.props.location.state.stayAvailable
        const locationState: LocationState = this.props.location.state

        navigate(`/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reserve/input`, {
            replace: true,
            state: {
                previousPageUrl: locationState.previousPageUrl,
                stayAvailable: isStayAvailable,
                reserveInfo: locationState.reserveInfo,
            },
        })
    }

    async handleRegisterButtonClick(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        const locationState: LocationState = this.props.location.state
        const isStayAvailable: boolean = locationState.stayAvailable
        const {condominiumCd, facilityId, reservationsRepo, navigate} = this.props

        try {
            let returnValue
            if (isStayAvailable) {
                returnValue = await NatAsyncCallFunction(reservationsRepo.reserveFacilityForOvernight.bind(
                    reservationsRepo,
                    condominiumCd,
                    facilityId,
                    locationState.reserveInfo as NewOvernightRentalFacilityReservation,
                ), navigate)
            } else {
                returnValue = await NatAsyncCallFunction(reservationsRepo.reserveFacilityForDuration.bind(
                    reservationsRepo,
                    condominiumCd,
                    facilityId,
                    locationState.reserveInfo as NewDurationRentalFacilityReservation,
                ), navigate)
            }
            if (returnValue === null) {
                return
            }

            setResidentMyReservationsPageSearchDate(locationState.reserveInfo.startDate)
            clearResidentReserveTopPageSearchConditions()
            clearResidentFacilityReservationStatusPageSearchDate()
            clearResidentFacilityReservationStatusPageFilterConditions()

            navigate(`/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reserve/complete`, {
                replace: true,
                state: {
                    transitionInputPage: true,
                    reserveType: locationState.reserveInfo.reserveType,
                },
            })
        } catch (e) {
            const httpError: HttpError = e as HttpError
            const {requestError, requestItemErrors} = httpError.responseBody
            const inputErrorResponseBody = new ErrorResponseBody(
                new RequestError(
                    requestError.messageId,
                    Messages.EFACC0015.toString(),
                    requestError.message,
                    requestError.type,
                ),
                requestItemErrors,
            )

            navigate(`/resident/condominiums/${condominiumCd}/facilities/${facilityId}/reserve/input`, {
                replace: true,
                state: {
                    previousPageUrl: locationState.previousPageUrl,
                    errorResponseBody: inputErrorResponseBody,
                    stayAvailable: isStayAvailable,
                    reserveInfo: locationState.reserveInfo,
                },
            })
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    async getCurrentDate(): Promise<Nullable<Moment>> {
        const {currentDateTimeRepo, navigate} = this.props
        let currentDate: Nullable<Moment>
        const currentTimeResponse = await NatAsyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            navigate,
        )
        if (currentTimeResponse === null || currentTimeResponse.details === null) {
            return null
        }
        currentDate = moment(currentTimeResponse.details.currentDateTime)
        return currentDate
    }

    render(): ReactNode {
        const {facilityInfo, reserveInfo} = this.props.location.state
        const {startDate, reservationTitle, sharedUseRoomNumberList, reserveType} = reserveInfo
        const {cancelAvailability, cancelReceptionLimitDate, cancelReceptionLimitTime} = facilityInfo
        const {currentDateTime} = this.state

        if (currentDateTime === null) {
            return null
        }
        if (reserveType !== RESERVATION_TYPE.FIRST_COME.value &&
            reserveType !== RESERVATION_TYPE.LOTTERY.value) {
            return null
        }

        let confirmFormDate: ReserveFacilityConfirmData
        let startDateTimeMoment: Moment

        if (facilityInfo.stayAvailability) {
            const {stayCount} = reserveInfo as NewOvernightRentalFacilityReservation
            confirmFormDate = {
                startDate: startDate,
                startTime: null,
                endDate: null,
                endTime: null,
                stayCount: stayCount,
                reservationTitle: reservationTitle,
                sharedUseRoomNumberList: sharedUseRoomNumberList,
                options: reserveInfo.options,
                paymentInformation: reserveInfo.paymentInformation,
            }
            const checkinTime = facilityInfo.overnightFacilitySetting?.checkinTime ?? null
            startDateTimeMoment = convertToMomentOrError(startDate, checkinTime)
        } else {
            const {startTime, endDate, endTime} = reserveInfo as NewDurationRentalFacilityReservation
            confirmFormDate = {
                startDate: startDate,
                startTime: startTime,
                endDate: endDate,
                endTime: endTime,
                stayCount: null,
                reservationTitle: reservationTitle,
                sharedUseRoomNumberList: sharedUseRoomNumberList,
                options: reserveInfo.options,
                paymentInformation: reserveInfo.paymentInformation,
            }
            startDateTimeMoment = convertToMomentOrError(startDate, startTime)
        }

        let noticeMessage: ReactNode
        let reservationTypeTag: ReactNode
        let registerButtonString: string
        if (RESERVATION_TYPE.FIRST_COME.value === reserveType) {
            noticeMessage = <>
                <NoticeMessageHeader>
                    予約は完了していません。
                </NoticeMessageHeader>
                <NoticeMessageBody>
                    予約内容をご確認のうえ、「この内容で予約する」ボタンを押してください。<br/>
                    ボタンを押すと予約を完了します。
                </NoticeMessageBody>
            </>
            reservationTypeTag = <ReservationTypeTagForFirstCome>
                {RESERVATION_TYPE.FIRST_COME.label}
            </ReservationTypeTagForFirstCome>
            registerButtonString = 'この内容で予約する'
        } else {    // RESERVATION_TYPE.LOTTERY.value === reserveType
            noticeMessage = <>
                <NoticeMessageHeader>
                    予約の申し込みは完了していません。
                </NoticeMessageHeader>
                <NoticeMessageBody>
                    予約内容をご確認のうえ、「この内容で申し込む」ボタンを押してください。<br/>
                    ボタンを押すと予約の申し込みを受け付けます。
                </NoticeMessageBody>
            </>
            reservationTypeTag = <ReservationTypeTagForLottery>
                {RESERVATION_TYPE.LOTTERY.label}
            </ReservationTypeTagForLottery>
            registerButtonString = 'この内容で申し込む'
        }

        let warningMessage: Optional<ReactNode>
        if (AVAILABLE_OR_NOT.UNAVAILABLE === cancelAvailability) {
            warningMessage =
                <div>
                    {Messages.WFACR0001.toString()}<br/>
                    {Messages.WFACR0003.toString()}
                </div>
        } else if (isCancelLimitOver(
            startDateTimeMoment,
            currentDateTime,
            cancelAvailability,
            cancelReceptionLimitDate,
            cancelReceptionLimitTime,
        )) {
            warningMessage =
                <div>
                    {Messages.WFACR0002.toString()}<br/>
                    {Messages.WFACR0003.toString()}
                </div>
        }

        return <>
            <PlainLinkStyledBackButton onClick={this.handleBackLinkOnClick}>戻る</PlainLinkStyledBackButton>
            <BlockSpacer height={'15px'}/>
            <DisplayItemDividerForPageTop/>
            <BlockSpacer height={'30px'}/>
            <StyledPageTitle>予約確認</StyledPageTitle>
            {noticeMessage}
            <BlockSpacer height={'20px'}/>
            {reservationTypeTag}
            <BlockSpacer height={'10px'}/>
            <ResidentReserveFacilityConfirmComponent
                facilityInfo={facilityInfo}
                reserveInfo={confirmFormDate}
            />
            {
                warningMessage ?
                    <>
                        <BlockSpacer height={'15px'}/>
                        <WarningMessageArea>
                            <FontAwesomeIcon icon={faExclamationCircle} color={'#F5A623'} size={'lg'}/>
                            <Box width={'5px'} minWidth={'5px'}/>
                            {warningMessage}
                        </WarningMessageArea>
                        <BlockSpacer height={'10px'}/>
                    </>
                    :
                    <BlockSpacer height={'30px'}/>
            }
            <PrimaryStyledButton onClick={this.handleRegisterButtonClick}>
                {registerButtonString}
            </PrimaryStyledButton>
            <BlockSpacer height={'30px'}/>
            <CenterPlainLinkStyledButton onClick={this.handleBackLinkOnClick}>修正する</CenterPlainLinkStyledButton>
            <BlockSpacer height={'15px'}/>
        </>
    }
}

export const NoticeMessageHeader = styled.div`
  margin-top: 10px;
  margin-bottom: 4px;
  font-size: ${FontSize.Body};
  font-weight: ${FontWeights.Bold};
`

export const NoticeMessageBody = styled.div`
  font-size: ${FontSize.Middle};
  font-weight: ${FontWeights.Normal};
`

export const ReservationTypeTag = styled.div`
  width: 84px;
  height: 24px;
  font-size: ${FontSize.Body};
  font-weight: ${FontWeights.Bold};
  display: flex;
  justify-content: center;
  color: white;
`

export const ReservationTypeTagForFirstCome = styled(ReservationTypeTag)`
  background: linear-gradient(to bottom right,
  rgb(0, 145, 136) 0%,
  rgb(0, 145, 136) 50%,
  #00838A 50%,
  #00838A 100%);
`

export const ReservationTypeTagForLottery = styled(ReservationTypeTag)`
  background: linear-gradient(to bottom right,
  rgb(239, 103, 35) 0%,
  rgb(239, 103, 35) 50%,
  rgb(216, 91, 28) 50%,
  rgb(216, 91, 28) 100%);
`

export const WarningMessageArea = styled.div`
  background: #FDEDD3;
  font-weight: ${FontWeights.Normal};
  font-size: ${FontSize.Middle};
  padding: 10px;
  display: flex;
  align-items: flex-start;
`

export default createResidentPage(ResidentReserveFacilityInputConfirmPage)
